import React from 'react';
import { Phone, Mail, MapPin, Clock } from 'lucide-react';
import ContactForm from '../components/ContactForm';
import SEO from '../components/SEO';

const Contact = () => {
  const contactInfo = [
    {
      icon: Phone,
      title: 'Phone',
      detail: '941-545-9057',
      link: 'tel:941-545-9057',
    },
    {
      icon: Mail,
      title: 'Email',
      detail: 'contact@paracca.com',
      link: 'mailto:contact@paracca.com',
    },
    {
      icon: MapPin,
      title: 'Address',
      detail: '428 S Tamiami Trail, Osprey, FL 34229',
      link: 'https://maps.google.com/?q=428+S+Tamiami+Trail+Osprey+FL+34229',
    },
    {
      icon: Clock,
      title: 'Office Hours',
      detail: 'Monday - Friday: 8:30 AM - 4:00 PM',
      link: null,
    },
  ];

  const schema = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "mainEntity": {
      "@type": "FinancialService",
      "name": "Paracca Financial Group",
      "telephone": "+1-941-545-9057",
      "email": "contact@paracca.com",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "428 S Tamiami Trail",
        "addressLocality": "Osprey",
        "addressRegion": "FL",
        "postalCode": "34229",
        "addressCountry": "US"
      }
    }
  };

  return (
    <>
      <SEO 
        title="Contact Us | Paracca Financial Group Sarasota FL"
        description="Contact Paracca Financial Group in Osprey, FL. Schedule a consultation with our financial advisors. Call 941-545-9057 or email contact@paracca.com."
        keywords="contact paracca financial, financial advisor sarasota contact, schedule consultation, osprey fl financial advisor"
        schema={schema}
      />

      {/* Hero Section */}
      <section className="relative h-[400px] overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('/hero-image.jpg')`,
          }}
        >
          <div className="absolute inset-0 bg-[#0a69b2] opacity-70"></div>
        </div>
        
        <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center">
          <div className="max-w-3xl">
            <h1 className="font-bellefair text-4xl md:text-6xl  text-white mb-6 uppercase" style={{ textShadow: '2px 2px 8px rgba(0,0,0,0.5)' }}>
              Contact Us
            </h1>
            <p className="font-montserrat text-lg md:text-xl text-white leading-relaxed" style={{ textShadow: '1px 1px 4px rgba(0,0,0,0.5)' }}>
              Schedule a complimentary consultation with our team of expert advisors
            </p>
          </div>
        </div>
      </section>

      {/* Contact Information Cards */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactInfo.map((item) => {
              const IconComponent = item.icon;
              return (
                <div 
                  key={item.title}
                  className="bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300 text-center"
                >
                  <div className="bg-[#0a69b2] w-14 h-14 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <IconComponent className="text-white" size={28} />
                  </div>
                  <h3 className="font-bellefair text-lg  text-[#0a69b2] mb-2 uppercase">
                    {item.title}
                  </h3>
                  {item.link ? (
                    <a 
                      href={item.link}
                      target={item.link.startsWith('http') ? '_blank' : undefined}
                      rel={item.link.startsWith('http') ? 'noopener noreferrer' : undefined}
                      className="font-montserrat text-gray-600 hover:text-[#0a69b2] transition-colors"
                    >
                      {item.detail}
                    </a>
                  ) : (
                    <p className="font-montserrat text-gray-600">
                      {item.detail}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-bellefair text-3xl md:text-4xl  text-[#0a69b2] mb-4 uppercase">
              Send Us a Message
            </h2>
            <p className="font-montserrat text-lg text-gray-600">
              Complete the form below and one of our advisors will contact you within 24 hours
            </p>
          </div>
          <div className="bg-gray-50 p-8 rounded-lg shadow-lg">
            <ContactForm />
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-bellefair text-3xl md:text-4xl  text-[#0a69b2] mb-4 uppercase">
              Visit Our Office
            </h2>
            <p className="font-montserrat text-lg text-gray-600">
              Located in Osprey, conveniently serving the Sarasota area
            </p>
          </div>
          <div className="rounded-lg overflow-hidden shadow-2xl">
            <iframe 
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3543.7684!2d-82.4864!3d27.1836!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x88c33e8e8e8e8e8e%3A0x8e8e8e8e8e8e8e8e!2s428%20S%20Tamiami%20Trail%2C%20Osprey%2C%20FL%2034229!5e0!3m2!1sen!2sus!4v1234567890"
              width="100%"
              height="450"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Paracca Financial Group Location"
            ></iframe>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#0a69b2] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-bellefair text-3xl md:text-4xl  mb-6 uppercase">
            Ready to Get Started?
          </h2>
          <p className="font-montserrat text-lg mb-8 leading-relaxed text-gray-100">
            Schedule your complimentary consultation today and discover how our holistic approach can transform your financial future
          </p>
          <a 
            href="tel:941-545-9057"
            className="inline-block bg-white text-[#0a69b2] hover:bg-gray-100 font-montserrat font-semibold text-lg px-10 py-4 rounded transition-all duration-300 hover:shadow-2xl"
          >
            Call 941-545-9057
          </a>
        </div>
      </section>
    </>
  );
};

export default Contact;
